/**
 * System Downtime Button Component
 *
 * PURPOSE: Report system downtime — pauses Session Health + auto-creates an
 * IT Support ticket on the backend (no opt-in, no duplicate UI).
 *
 * - Employees REPORT system downtime via a premium reason menu
 * - ONBEHALF-001: Tax/Title-Search Team Leaders (and Admin) can report
 *   downtime on behalf of their OWN team members and resolve only the
 *   reports they personally created. IT/Admin resolve everything as before.
 * - Employees see a return banner when downtime was reported on their behalf.
 * - FLICKER-FIX: DropdownMenu uses modal={false} so opening the menu never
 *   removes the body scrollbar (root cause of the page-width jump).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { usePoller } from '../../hooks/usePoller';
import {
  AlertTriangle, Zap, ZapOff, Loader2, ChevronDown, Server, Monitor,
  Cloud, HardDrive, WifiOff, Phone, Users, CheckCircle2, X, Search,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Icon + accent styling per downtime reason (premium chips)
const REASON_META = {
  system_outage:       { Icon: Server,        chip: 'bg-red-50 text-red-600 ring-red-100' },
  network_vpn:         { Icon: WifiOff,       chip: 'bg-sky-50 text-sky-600 ring-sky-100' },
  external_dependency: { Icon: Cloud,         chip: 'bg-violet-50 text-violet-600 ring-violet-100' },
  software_crash:      { Icon: Monitor,       chip: 'bg-orange-50 text-orange-600 ring-orange-100' },
  hardware_issue:      { Icon: HardDrive,     chip: 'bg-slate-100 text-slate-600 ring-slate-200' },
  other:               { Icon: AlertTriangle, chip: 'bg-amber-50 text-amber-600 ring-amber-100' },
};
const metaFor = (id) => REASON_META[id] || REASON_META.other;

const formatTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const formatClock = (iso) => {
  try {
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local time-only
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

// ─── Premium reason row (shared by menu + on-behalf dialog) ───
const ReasonRow = ({ reason, onSelect, selected, testIdPrefix = 'downtime-reason' }) => {
  const { Icon, chip } = metaFor(reason.id);
  return (
    <button
      type="button"
      onClick={() => onSelect(reason)}
      className={`w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-all duration-150 group border ${
        selected
          ? 'border-amber-300 bg-amber-50/70 ring-1 ring-amber-200'
          : 'border-transparent hover:border-slate-200 hover:bg-slate-50/80'
      }`}
      data-testid={`${testIdPrefix}-${reason.id}`}
    >
      <span className={`h-8 w-8 rounded-lg flex items-center justify-center ring-1 flex-shrink-0 transition-transform duration-150 group-hover:scale-105 ${chip}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-800 leading-tight">{reason.name}</span>
        {reason.description && (
          <span className="block text-[11px] text-slate-400 truncate leading-tight mt-0.5">{reason.description}</span>
        )}
      </span>
      {selected && <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0" />}
    </button>
  );
};

const SystemDowntimeButton = () => {
  const [downtimeStatus, setDowntimeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  // ONBEHALF-001 state
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState(null);
  const [obMember, setObMember] = useState(null);
  const [obSearch, setObSearch] = useState('');
  const [obListOpen, setObListOpen] = useState(false);
  const [obReason, setObReason] = useState(null);
  const [obDescription, setObDescription] = useState('');
  const [obSubmitting, setObSubmitting] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [bannerDismissing, setBannerDismissing] = useState(false);

  const fetchStatus = useCallback(async (signal) => {
    try {
      const response = await axios.get(`${API}/api/it-downtime/status`, { signal });
      setDowntimeStatus(response.data);
      if (response.data.active_issue) {
        setElapsedTime(response.data.active_issue.elapsed_minutes || 0);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.warn('Downtime status poll exceeded its 25s window and was cancelled — retrying with backoff');
        return false;
      }
      console.error('Error fetching downtime status:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware polling.
  usePoller(fetchStatus, 30000, { immediate: false });

  useEffect(() => {
    let timer;
    if (downtimeStatus?.active_issue) {
      timer = setInterval(() => setElapsedTime(prev => prev + 1), 60000);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [downtimeStatus?.active_issue]);

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    if (reason.id === 'other') {
      setDescription('');
      setShowDescriptionModal(true);
    } else {
      handleReportDowntime(reason.id, null);
    }
  };

  const handleReportDowntime = async (reasonId, desc) => {
    try {
      setReporting(true);
      const response = await axios.post(`${API}/api/it-downtime/report`, {
        reason_id: reasonId,
        description: desc
      });
      const ticketNumber = response?.data?.linked_ticket?.ticket_number;
      toast.success('System Downtime Reported', {
        description: ticketNumber
          ? `IT ticket ${ticketNumber} created. IT Manager + IT Support have been notified.`
          : 'Session tracking paused. IT has been notified to resolve.'
      });
      setElapsedTime(0);
      setShowDescriptionModal(false);
      setDescription('');
      setSelectedReason(null);
      fetchStatus();
    } catch (err) {
      console.error('Error reporting downtime:', err);
      toast.error(err.response?.data?.detail || 'Failed to report downtime');
    } finally {
      setReporting(false);
    }
  };

  const handleDescriptionSubmit = () => {
    if (!description.trim() && selectedReason?.id === 'other') {
      toast.error('Please provide a description for "Other" issues');
      return;
    }
    handleReportDowntime(selectedReason.id, description.trim() || null);
  };

  // ─── ONBEHALF-001 handlers ───
  const openMemberModal = async () => {
    setObMember(null);
    setObReason(null);
    setObDescription('');
    setObSearch('');
    setObListOpen(false);
    setShowMemberModal(true);
    setTeamMembers(null);
    try {
      const res = await axios.get(`${API}/api/it-downtime/team-members`);
      setTeamMembers(res.data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team members');
      setTeamMembers([]);
    }
  };

  const handleOnBehalfSubmit = async () => {
    if (!obMember || !obReason) return;
    if (obReason.id === 'other' && !obDescription.trim()) {
      toast.error('Please provide a description for "Other" issues');
      return;
    }
    try {
      setObSubmitting(true);
      const res = await axios.post(`${API}/api/it-downtime/report-for-member`, {
        member_id: obMember.id,
        reason_id: obReason.id,
        description: obDescription.trim() || null,
      });
      const ticketNumber = res?.data?.linked_ticket?.ticket_number;
      toast.success(`Downtime reported for ${obMember.full_name || obMember.email}`, {
        description: ticketNumber ? `IT ticket ${ticketNumber} created. IT has been notified.` : 'IT has been notified.',
      });
      setShowMemberModal(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report downtime for team member');
    } finally {
      setObSubmitting(false);
    }
  };

  const handleResolveOnBehalf = async (issue) => {
    try {
      setResolvingId(issue.id);
      const res = await axios.post(`${API}/api/it-downtime/resolve`, {
        downtime_id: issue.id,
        resolution_notes: 'Resolved by reporting Team Leader',
      });
      if (res.data.already_resolved) {
        toast.info('Already resolved', { description: 'IT had already resolved this downtime via the linked ticket.' });
      } else {
        toast.success(`Downtime resolved for ${issue.user_name}`, {
          description: `Duration: ${formatTime(res.data.duration_minutes || 0)}`,
        });
      }
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve downtime');
    } finally {
      setResolvingId(null);
    }
  };

  const dismissBanner = async (notice) => {
    try {
      setBannerDismissing(true);
      await axios.post(`${API}/api/it-downtime/on-behalf-ack`, { downtime_id: notice.id });
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setBannerDismissing(false);
    }
  };

  // ─── Employee return banner (rendered for every state incl. active) ───
  const notice = downtimeStatus?.on_behalf_notice;
  const onBehalfBanner = notice ? (
    <div
      className="fixed top-[64px] left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 shadow-lg shadow-amber-900/10 pl-4 pr-2 py-2.5 max-w-[92vw]"
      data-testid="on-behalf-downtime-banner"
    >
      <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
        <ZapOff className="w-4 h-4" />
      </span>
      <div className="text-[12.5px] text-amber-900 leading-snug">
        <span className="font-semibold">Downtime reported by {notice.reported_by_name}</span>
        {' '}at {formatClock(notice.start_time)} — {notice.reason_name}
        <span className="block text-[11px] text-amber-700/80">
          {notice.status === 'active'
            ? 'Currently active — session tracking is paused on your record.'
            : `Resolved${notice.duration_minutes != null ? ` after ${formatTime(notice.duration_minutes)}` : ''} — recorded on your time record.`}
        </span>
      </div>
      <button
        type="button"
        onClick={() => dismissBanner(notice)}
        disabled={bannerDismissing}
        className="h-7 w-7 rounded-lg flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors flex-shrink-0"
        title="Acknowledge & dismiss"
        data-testid="on-behalf-banner-dismiss"
      >
        {bannerDismissing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-4 h-4" />}
      </button>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="w-8 h-8 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not clocked in
  if (!downtimeStatus?.is_clocked_in) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" disabled className="gap-1 opacity-50 cursor-not-allowed">
              <ZapOff className="w-4 h-4" />
              <span className="hidden sm:inline">Downtime</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-medium">Clock in to report system downtime</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Active downtime - Employee sees STATUS ONLY (no resolve button)
  if (downtimeStatus?.active_issue) {
    const activeIssue = downtimeStatus.active_issue;
    const isLong = elapsedTime > 240;
    const isExtended = elapsedTime > 60;

    return (
      <>
        {onBehalfBanner}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-default ${
                  isLong
                    ? 'border border-red-300 bg-red-50 text-red-700'
                    : isExtended
                      ? 'border border-orange-300 bg-orange-50 text-orange-700'
                      : 'border border-amber-300 bg-amber-50 text-amber-700'
                }`}
                data-testid="system-downtime-status"
              >
                <ZapOff className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">Downtime</span>
                <Badge
                  variant="secondary"
                  className={`font-mono text-xs ${
                    isLong ? 'bg-red-200 text-red-800' : isExtended ? 'bg-orange-200 text-orange-800' : ''
                  }`}
                >
                  {formatTime(elapsedTime)}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-amber-600">
                  <ZapOff className="w-4 h-4" />
                  <span className="font-medium">System Downtime Active</span>
                </div>
                <p className="text-sm"><span className="font-medium">Reason:</span> {activeIssue.reason_name}</p>
                {activeIssue.reported_on_behalf && (
                  <p className="text-sm text-amber-600 font-medium">
                    Reported by {activeIssue.reported_by_name} on your behalf
                  </p>
                )}
                {activeIssue.description && (
                  <p className="text-sm text-gray-400">{activeIssue.description}</p>
                )}
                <p className="text-sm"><span className="font-medium">Duration:</span> {formatTime(elapsedTime)}</p>
                <div className="pt-1 border-t border-gray-200">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    IT will mark resolved when fixed
                  </p>
                </div>
                {isLong && <p className="text-xs text-red-500 font-medium">⚠️ Extended downtime – IT should review</p>}
                {isExtended && !isLong && <p className="text-xs text-orange-500">⚠️ Over 1 hour – contact IT if unresolved</p>}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </>
    );
  }

  // No active downtime - show the premium report menu
  const downtimeCount = downtimeStatus?.issues_today?.length || 0;
  const canReportOnBehalf = downtimeStatus?.can_report_on_behalf;
  const myOnBehalfActive = downtimeStatus?.my_on_behalf_active || [];

  return (
    <>
      {onBehalfBanner}
      {/* FLICKER-FIX: modal={false} keeps the body scrollbar in place — no
          scroll lock, no page-width jump, no layout shift on open/close. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            disabled={reporting}
            data-testid="report-downtime-btn"
          >
            {reporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            <span className="hidden sm:inline">Downtime</span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-[340px] p-0 overflow-hidden rounded-xl border border-slate-200/80 shadow-xl shadow-slate-900/10"
          data-testid="downtime-menu"
        >
          {/* Header */}
          <div className="px-4 pt-3.5 pb-3 bg-gradient-to-br from-slate-50 to-amber-50/40 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-600 ring-1 ring-amber-200/70 flex items-center justify-center">
                  <Zap className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-[13px] font-bold text-slate-800 leading-tight tracking-tight">Report System Downtime</p>
                  <p className="text-[11px] text-slate-400 leading-tight mt-0.5">Pauses session tracking until IT resolves</p>
                </div>
              </div>
              {downtimeCount > 0 && (
                <Badge variant="outline" className="text-[10px] font-medium text-slate-500 bg-white">
                  {downtimeCount} today
                </Badge>
              )}
            </div>
          </div>

          {/* Reasons */}
          <div className="p-2 space-y-0.5">
            {downtimeStatus?.reasons?.map((reason) => (
              <ReasonRow key={reason.id} reason={reason} onSelect={handleReasonSelect} />
            ))}
          </div>

          {/* Team Leader — report on behalf */}
          {canReportOnBehalf && (
            <div className="border-t border-slate-100 p-2">
              <p className="px-2.5 pt-1 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Team</p>
              <button
                type="button"
                onClick={openMemberModal}
                className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-left border border-transparent hover:border-blue-200 hover:bg-blue-50/60 transition-all duration-150 group"
                data-testid="report-for-member-btn"
              >
                <span className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100 flex items-center justify-center flex-shrink-0 transition-transform duration-150 group-hover:scale-105">
                  <Users className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-slate-800 leading-tight">Report for Team Member</span>
                  <span className="block text-[11px] text-slate-400 leading-tight mt-0.5">When their system is down and they can&apos;t report it</span>
                </span>
              </button>

              {myOnBehalfActive.length > 0 && (
                <div className="mt-1.5 space-y-1" data-testid="my-on-behalf-list">
                  {myOnBehalfActive.map((ob) => (
                    <div
                      key={ob.id}
                      className="flex items-center gap-2.5 rounded-lg bg-amber-50/60 border border-amber-200/70 px-2.5 py-2"
                      data-testid={`on-behalf-active-${ob.id}`}
                    >
                      <ZapOff className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-700 truncate leading-tight">{ob.user_name}</p>
                        <p className="text-[10.5px] text-slate-400 truncate leading-tight">{ob.reason_name} · {formatTime(ob.elapsed_minutes || 0)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-[11px] font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        disabled={resolvingId === ob.id}
                        onClick={() => handleResolveOnBehalf(ob)}
                        data-testid={`resolve-on-behalf-${ob.id}`}
                      >
                        {resolvingId === ob.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Resolve'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Description Modal for "Other" reason */}
      <Dialog open={showDescriptionModal} onOpenChange={setShowDescriptionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ZapOff className="w-5 h-5 text-amber-600" />
              Report System Downtime
            </DialogTitle>
            <DialogDescription>Describe the issue preventing you from working.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Textarea
                placeholder="Describe the issue (required for 'Other')"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[100px]"
                maxLength={500}
                data-testid="downtime-description"
              />
              <p className="text-xs text-gray-500 mt-1 text-right">{description.length}/500</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="downtime-desc-cancel-btn"
              onClick={() => { setShowDescriptionModal(false); setSelectedReason(null); setDescription(''); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDescriptionSubmit}
              disabled={reporting || (!description.trim() && selectedReason?.id === 'other')}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="downtime-desc-submit-btn"
            >
              {reporting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Report Downtime
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ONBEHALF-001 — Report for Team Member modal (compact) */}
      <Dialog open={showMemberModal} onOpenChange={(o) => { if (!obSubmitting) setShowMemberModal(o); }}>
        <DialogContent className="max-w-[430px] p-5 gap-3" data-testid="report-for-member-modal">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                <Users className="w-3.5 h-3.5" />
              </span>
              Report Downtime for Team Member
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-snug">
              Recorded on the employee&apos;s session — they&apos;ll see a visible banner on return.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Team member — searchable dropdown */}
            <div>
              <p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider mb-1">Team member</p>
              {obMember ? (
                <div
                  className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-2.5 py-1.5"
                  data-testid="selected-member"
                >
                  <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {(obMember.full_name || obMember.email || '?').slice(0, 1).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] font-semibold text-slate-800 truncate leading-tight">{obMember.full_name || obMember.email}</span>
                    <span className="block text-[10.5px] text-slate-400 truncate leading-tight">{obMember.role}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => { setObMember(null); setObSearch(''); setObListOpen(false); }}
                    className="h-6 w-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white transition-colors flex-shrink-0"
                    title="Change member"
                    data-testid="change-member-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={obSearch}
                    onChange={(e) => { setObSearch(e.target.value); setObListOpen(true); }}
                    onFocus={() => setObListOpen(true)}
                    onBlur={() => setTimeout(() => setObListOpen(false), 150)}
                    placeholder="Search by name or email…"
                    className="w-full h-8 rounded-lg border border-slate-200 bg-white pl-8 pr-2.5 text-[12.5px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 transition-colors"
                    data-testid="member-search-input"
                  />
                  {obListOpen && (
                    <div
                      className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-44 overflow-y-auto p-1"
                      data-testid="member-list"
                    >
                      {teamMembers === null ? (
                        <div className="flex items-center gap-2 text-[12px] text-slate-400 px-2.5 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading team…</div>
                      ) : (() => {
                        const q = obSearch.trim().toLowerCase();
                        const filtered = teamMembers.filter(m =>
                          !q || (m.full_name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
                        if (filtered.length === 0) {
                          return <p className="text-[12px] text-slate-400 italic px-2.5 py-2">No matching team members</p>;
                        }
                        return filtered.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            disabled={m.has_active_downtime}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setObMember(m); setObListOpen(false); }}
                            className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
                              m.has_active_downtime ? 'opacity-45 cursor-not-allowed' : 'hover:bg-slate-50'
                            }`}
                            data-testid={`member-option-${m.id}`}
                          >
                            <span className="h-5.5 w-5.5 min-h-[22px] min-w-[22px] rounded-full bg-slate-100 text-slate-500 text-[9.5px] font-bold flex items-center justify-center flex-shrink-0">
                              {(m.full_name || m.email || '?').slice(0, 1).toUpperCase()}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-[12px] font-medium text-slate-700 truncate leading-tight">{m.full_name || m.email}</span>
                              <span className="block text-[10px] text-slate-400 truncate leading-tight">{m.role}</span>
                            </span>
                            {m.has_active_downtime && (
                              <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300 px-1 py-0">In downtime</Badge>
                            )}
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reason — compact 2-column grid */}
            <div>
              <p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider mb-1">Reason</p>
              <div className="grid grid-cols-2 gap-1.5">
                {downtimeStatus?.reasons?.map((reason) => {
                  const { Icon, chip } = metaFor(reason.id);
                  const selected = obReason?.id === reason.id;
                  return (
                    <button
                      key={reason.id}
                      type="button"
                      onClick={() => setObReason(reason)}
                      className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all duration-150 ${
                        selected
                          ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`ob-reason-${reason.id}`}
                    >
                      <span className={`h-6 w-6 rounded-md flex items-center justify-center ring-1 flex-shrink-0 ${chip}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[11.5px] font-semibold text-slate-700 truncate">{reason.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description — compact */}
            <div>
              <p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Description {obReason?.id === 'other' ? <span className="text-red-500 normal-case">(required)</span> : <span className="text-slate-400 normal-case font-medium">(optional)</span>}
              </p>
              <Textarea
                placeholder={obReason?.id === 'other' ? "Describe the issue (required for 'Other')" : 'Add context for IT (optional)'}
                value={obDescription}
                onChange={(e) => setObDescription(e.target.value)}
                className="min-h-[52px] text-[12.5px] resize-none"
                rows={2}
                maxLength={500}
                data-testid="ob-description"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowMemberModal(false)} disabled={obSubmitting} data-testid="ob-cancel-btn">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleOnBehalfSubmit}
              disabled={obSubmitting || !obMember || !obReason || (obReason?.id === 'other' && !obDescription.trim())}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="ob-submit-btn"
            >
              {obSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Report Downtime
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SystemDowntimeButton;
